import { useState } from 'react';
import { repriseApi } from '../../api/resources';
import { Bar } from '../../components/ui/Bar.jsx';
import { Btn } from '../../components/ui/Btn.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { AIDE_REPRISE, TYPES_REPRISE, lisCsv, modeleCsv } from '../../lib/reprise';
import { C } from '../../theme/palette';

/** Lignes montrées dans l'aperçu : assez pour reconnaître le fichier. */
const APERCU_LIGNES = 5;

/** Enregistre le modèle CSV d'un type sur l'appareil. */
function telechargeModele(type) {
  const blob = new Blob([modeleCsv(type)], { type: 'text/csv;charset=utf-8' });
  const adresse = URL.createObjectURL(blob);
  const lien = document.createElement('a');
  lien.href = adresse;
  lien.download = 'modele-reprise-' + type + '.csv';
  document.body.appendChild(lien);
  lien.click();
  lien.remove();
  setTimeout(() => URL.revokeObjectURL(adresse), 10_000);
}

/** Lit un fichier texte (UTF-8), y compris sur les navigateurs sans `Blob.text()`. */
const litTexte = (f) =>
  new Promise((resolve, reject) => {
    const lecteur = new FileReader();
    lecteur.onload = () => resolve(String(lecteur.result || ''));
    lecteur.onerror = () => reject(lecteur.error);
    lecteur.readAsText(f, 'utf-8');
  });

const encadre = (couleur, fond) => ({
  background: fond,
  border: '1px solid ' + couleur + '40',
  color: couleur,
  borderRadius: 8,
  padding: '8px 10px',
  fontSize: 12,
  marginTop: 8,
  lineHeight: 1.5,
});

/**
 * Carte « Reprendre mon ancien logiciel » : import en masse, depuis un CSV,
 * des clients, devis, factures et dépenses d'un autre logiciel, dates
 * d'origine conservées.
 *
 * <p>Trois temps : choisir le type (et télécharger son modèle), choisir le
 * fichier — le serveur rend aussitôt un aperçu, lignes reconnues et erreurs,
 * sans rien écrire —, puis importer. Un fichier qui porte une erreur ne
 * s'importe pas : on le corrige et on le reprend en entier.
 *
 * @returns {import('react').ReactElement} la carte
 */
export function RepriseCard({ deplie = false }) {
  // Comme la maquette, la carte présente d'abord un seul bouton ; l'outil
  // d'import se déplie dessous quand on le touche.
  const [ouvert, setOuvert] = useState(deplie);
  const [type, setType] = useState('clients');
  const [fichier, setFichier] = useState(null);
  const [apercu, setApercu] = useState(null);
  const [resultat, setResultat] = useState(null);
  const [erreur, setErreur] = useState('');
  const [enCours, setEnCours] = useState(false);

  const reinitialise = () => {
    setFichier(null);
    setApercu(null);
    setErreur('');
  };

  const choisitType = (id) => {
    setType(id);
    setResultat(null);
    reinitialise();
  };

  const choisitFichier = async (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!f) return;
    reinitialise();
    setResultat(null);
    if (/\.xlsx?$/i.test(f.name)) {
      setErreur(
        'Enregistre d’abord ton classeur au format CSV (Excel : Fichier → Enregistrer sous → CSV), puis choisis ce fichier.',
      );
      return;
    }
    const { colonnes, lignes } = lisCsv(await litTexte(f));
    if (!lignes.length) {
      setErreur('Ce fichier ne contient aucune ligne sous l’en-tête.');
      return;
    }
    setEnCours(true);
    try {
      const compteRendu = await repriseApi.envoie(type, f.name, lignes, true);
      setFichier({ nom: f.name, colonnes, lignes });
      setApercu(compteRendu);
    } catch (ex) {
      setErreur(ex?.message || 'L’aperçu n’a pas pu être établi.');
    } finally {
      setEnCours(false);
    }
  };

  const importe = async () => {
    if (!fichier) return;
    setEnCours(true);
    try {
      const compteRendu = await repriseApi.envoie(type, fichier.nom, fichier.lignes, false);
      if (compteRendu.erreurs?.length) {
        setApercu(compteRendu);
      } else {
        setResultat(compteRendu);
        reinitialise();
      }
    } catch (ex) {
      setErreur(ex?.message || 'L’import a échoué : rien n’a été enregistré.');
    } finally {
      setEnCours(false);
    }
  };

  const erreurs = apercu?.erreurs || [];
  const importable = apercu && !erreurs.length && apercu.importees > 0;

  return (
    <Card style={{ marginBottom: 12 }}>
      <Bar
        icon="📥"
        title="Reprendre mon ancien logiciel"
        color={C.teal}
        sub="Migre tes clients, devis, factures et dépenses"
      />
      <div style={{ padding: 13 }}>
        <div style={{ fontSize: 11.5, color: C.grey, lineHeight: 1.5, marginBottom: 10 }}>
          Importe d’un coup tout l’historique de ton ancien logiciel (Excel/CSV) : clients · devis ·
          factures · dépenses. Les dates d’origine sont gardées, rangées année par année.
        </div>
        {!ouvert && (
          <Btn
            label="📥 Reprendre mon ancien logiciel"
            color={C.teal}
            full
            onClick={() => setOuvert(true)}
          />
        )}
        {ouvert && (
          <>
            <div
              role="radiogroup"
              aria-label="Que contient le fichier ?"
              style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}
            >
              {TYPES_REPRISE.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  role="radio"
                  aria-checked={type === t.id}
                  onClick={() => choisitType(t.id)}
                  style={{
                    padding: '8px 12px',
                    borderRadius: 8,
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: 'pointer',
                    minHeight: 40,
                    border: '1px solid ' + (type === t.id ? C.teal : C.border),
                    background: type === t.id ? C.teal : C.white,
                    color: type === t.id ? '#fff' : C.grey,
                  }}
                >
                  {t.libelle}
                </button>
              ))}
            </div>
            <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.5, marginBottom: 10 }}>
              {AIDE_REPRISE[type]}
              {type !== 'clients' && type !== 'depenses' && ' Reprends d’abord tes clients.'}
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 160 }}>
                <Btn
                  label="⬇️ Télécharger le modèle CSV"
                  sm
                  ghost
                  color={C.navy}
                  full
                  onClick={() => telechargeModele(type)}
                />
              </div>
              <label
                style={{
                  flex: 1,
                  minWidth: 160,
                  textAlign: 'center',
                  cursor: 'pointer',
                  fontSize: 12,
                  fontWeight: 800,
                  color: C.teal,
                  border: '1.5px solid ' + C.teal,
                  borderRadius: 9,
                  padding: '9px',
                }}
              >
                {enCours && !apercu ? 'Lecture…' : '📂 Choisir le fichier (CSV)'}
                <input
                  type="file"
                  accept=".csv,.txt,text/csv,.xlsx,.xls"
                  aria-label="Fichier à reprendre"
                  style={{ display: 'none' }}
                  onChange={choisitFichier}
                />
              </label>
            </div>

            {erreur && (
              <div role="alert" style={encadre(C.red, C.redL)}>
                {erreur}
              </div>
            )}

            {apercu && fichier && (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: C.ink, marginBottom: 6 }}>
                  Aperçu de « {fichier.nom} » : {apercu.lues} ligne(s) lue(s), {apercu.importees}{' '}
                  reconnue(s)
                  {apercu.ignorees ? ', ' + apercu.ignorees + ' ignorée(s)' : ''}
                  {erreurs.length ? ', ' + erreurs.length + ' erreur(s)' : ''}.
                </div>
                <div
                  style={{ overflowX: 'auto', border: '1px solid ' + C.border, borderRadius: 8 }}
                >
                  <table style={{ borderCollapse: 'collapse', fontSize: 11, width: '100%' }}>
                    <thead>
                      <tr>
                        {fichier.colonnes.map((c) => (
                          <th
                            key={c}
                            style={{
                              textAlign: 'left',
                              padding: '5px 7px',
                              background: C.tealL,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {c}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {fichier.lignes.slice(0, APERCU_LIGNES).map((l, i) => (
                        <tr key={i}>
                          {fichier.colonnes.map((c) => (
                            <td
                              key={c}
                              style={{
                                padding: '5px 7px',
                                borderTop: '1px solid ' + C.border,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {l[c]}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {fichier.lignes.length > APERCU_LIGNES && (
                  <div style={{ fontSize: 8, color: C.muted, marginTop: 4 }}>
                    … et {fichier.lignes.length - APERCU_LIGNES} autre(s) ligne(s).
                  </div>
                )}
                {erreurs.length > 0 && (
                  <div role="alert" style={encadre(C.red, C.redL)}>
                    <b>Rien ne sera importé tant que ces lignes ne sont pas corrigées :</b>
                    <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                      {erreurs.slice(0, 50).map((er, i) => (
                        <li key={i}>
                          Ligne {er.ligne} : {er.message}
                        </li>
                      ))}
                    </ul>
                    {erreurs.length > 50 && <div>… et {erreurs.length - 50} autre(s).</div>}
                  </div>
                )}
                {(apercu.avertissements || []).length > 0 && (
                  <div style={encadre(C.amber, C.amberL)}>
                    {apercu.avertissements.slice(0, 20).map((a, i) => (
                      <div key={i}>{a}</div>
                    ))}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
                  <div style={{ flex: 1 }}>
                    <Btn
                      label={
                        enCours
                          ? 'Import en cours…'
                          : '📥 Importer ' + apercu.importees + ' ligne(s)'
                      }
                      color={C.teal}
                      full
                      disabled={!importable || enCours}
                      onClick={importe}
                    />
                  </div>
                  <Btn label="Annuler" sm ghost color={C.grey} onClick={reinitialise} />
                </div>
              </div>
            )}

            {resultat && (
              <div role="status" style={encadre(C.green, C.greenL)}>
                ✓ Reprise terminée : {resultat.importees} ligne(s) importée(s)
                {resultat.ignorees
                  ? ', ' + resultat.ignorees + ' ignorée(s) (déjà présentes)'
                  : ''}{' '}
                depuis « {resultat.fichier} ». Recharge la page pour les voir dans les listes.
              </div>
            )}
          </>
        )}
      </div>
    </Card>
  );
}
